/* ica-quote.js — Unified Cart client-side logic
   Loaded with `defer` via theme chrome. Vanilla JS, no jQuery.

   B3 Fix 2 — ONE top-right cart. The drawer is now a single surface with up
   to two sections:
     • Quote items   — RFQ products held in localStorage (ica_quote_basket_v1)
     • Buy now items — checkout products in the native Shopify cart (/cart.js)
   The header badge count = native cart.item_count + localStorage quote count.
   commerce_mode (rfq/checkout) is unchanged — this is purely the UX surface.

   Modules (IIFE-scoped, exposed on window.icaQuote for QA hooks):
     - Store    : localStorage CRUD with sessionStorage fallback, 30-day prune
     - Cart     : native Shopify cart mirror (seed island + /cart.js + change.js)
     - Drawer   : open/close, focus trap, ESC + backdrop dismiss, dual-section render
     - Badge    : COMBINED count + visibility sync, pulse on add
     - Toast    : slide-in/out, auto-dismiss, "View cart" link
     - Add      : wire Add-to-Quote button clicks, store + toast + event

   Fires four new BRDG events via window.icaEvents.fire (no counter increments).
     11. quote_basket_add
     12. quote_basket_remove
     13. quote_basket_view
     14. quote_basket_submitted

   Silent degradation: if localStorage + sessionStorage both fail, Add-to-Quote
   buttons are rewritten to per-product /pages/talk-to-a-coating-expert?product=...
   routing. NO user-visible caveat copy (Michael Q12 amendment).
*/

(function () {
  'use strict';

  var STORAGE_KEY = 'ica_quote_basket_v1';
  var EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  var QTY_DEBOUNCE_MS = 300;
  // Routing handle is the new "coating expert" page (B3 Fix 4). The 301 from
  // the old handle catches stragglers, but route directly here.
  var QUOTE_PAGE = '/pages/talk-to-a-coating-expert';

  /* ==========================================================================
     STORAGE BACKEND — try localStorage, fall back to sessionStorage, else null
     ========================================================================*/
  function makeBackend() {
    function testStore(store) {
      try {
        var k = '__ica_test__';
        store.setItem(k, '1');
        store.removeItem(k);
        return true;
      } catch (e) {
        return false;
      }
    }
    if (typeof window === 'undefined') return null;
    if (window.localStorage && testStore(window.localStorage)) return window.localStorage;
    if (window.sessionStorage && testStore(window.sessionStorage)) return window.sessionStorage;
    return null;
  }

  var backend = makeBackend();
  var storageAvailable = backend !== null;

  /* ==========================================================================
     STORE — basket CRUD
     ========================================================================*/
  var Store = {
    listeners: [],

    on: function (fn) {
      if (typeof fn === 'function') this.listeners.push(fn);
    },

    _notify: function () {
      var basket = this.getBasket();
      for (var i = 0; i < this.listeners.length; i++) {
        try { this.listeners[i](basket); } catch (e) { /* silent */ }
      }
    },

    _read: function () {
      if (!backend) return [];
      try {
        var raw = backend.getItem(STORAGE_KEY);
        if (!raw) return [];
        var parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed;
      } catch (e) {
        // Corruption — reset silently
        try { backend.removeItem(STORAGE_KEY); } catch (er) { /* silent */ }
        console.warn('[ica-quote] basket parse failure; reset to empty');
        return [];
      }
    },

    _write: function (basket) {
      if (!backend) return;
      try {
        backend.setItem(STORAGE_KEY, JSON.stringify(basket));
      } catch (e) {
        // Quota or unavailable; silent
      }
    },

    _prune: function (basket) {
      var now = Date.now();
      var fresh = [];
      for (var i = 0; i < basket.length; i++) {
        var item = basket[i];
        var addedAt = item && item.addedAt ? Date.parse(item.addedAt) : NaN;
        if (isNaN(addedAt)) {
          // Missing timestamp — treat as fresh, stamp it
          item.addedAt = new Date().toISOString();
          fresh.push(item);
        } else if (now - addedAt < EXPIRY_MS) {
          fresh.push(item);
        }
        // else: stale, drop silently
      }
      return fresh;
    },

    getBasket: function () {
      var raw = this._read();
      var pruned = this._prune(raw);
      if (pruned.length !== raw.length) {
        this._write(pruned);
      }
      return pruned;
    },

    size: function () {
      return this.getBasket().length;
    },

    has: function (handle) {
      var basket = this.getBasket();
      for (var i = 0; i < basket.length; i++) {
        if (basket[i].handle === handle) return true;
      }
      return false;
    },

    getItem: function (handle) {
      var basket = this.getBasket();
      for (var i = 0; i < basket.length; i++) {
        if (basket[i].handle === handle) return basket[i];
      }
      return null;
    },

    addItem: function (item) {
      if (!item || !item.handle) return { result: 'invalid', basket: this.getBasket() };
      var basket = this.getBasket();
      for (var i = 0; i < basket.length; i++) {
        if (basket[i].handle === item.handle) {
          basket[i].qty = Math.min(999, (basket[i].qty || 1) + 1);
          basket[i].addedAt = new Date().toISOString(); // refresh expiry
          this._write(basket);
          this._notify();
          return { result: 'incremented', item: basket[i], basket: basket };
        }
      }
      var newItem = {
        handle: item.handle,
        product_gid: item.product_gid || null,
        title: item.title || item.handle,
        brand: item.brand || '',
        product_line: item.product_line || '',
        image_src: item.image_src || '',
        qty: 1,
        notes: '',
        addedAt: new Date().toISOString()
      };
      basket.push(newItem);
      this._write(basket);
      this._notify();
      return { result: 'added', item: newItem, basket: basket };
    },

    removeItem: function (handle) {
      var basket = this.getBasket();
      var next = [];
      for (var i = 0; i < basket.length; i++) {
        if (basket[i].handle !== handle) next.push(basket[i]);
      }
      this._write(next);
      this._notify();
      return next;
    },

    setQty: function (handle, qty) {
      qty = parseInt(qty, 10);
      if (isNaN(qty) || qty < 1) qty = 1;
      if (qty > 999) qty = 999;
      var basket = this.getBasket();
      for (var i = 0; i < basket.length; i++) {
        if (basket[i].handle === handle) {
          basket[i].qty = qty;
          this._write(basket);
          this._notify();
          return basket[i];
        }
      }
      return null;
    },

    setNotes: function (handle, notes) {
      if (typeof notes !== 'string') notes = '';
      if (notes.length > 500) notes = notes.slice(0, 500);
      var basket = this.getBasket();
      for (var i = 0; i < basket.length; i++) {
        if (basket[i].handle === handle) {
          basket[i].notes = notes;
          this._write(basket);
          this._notify();
          return basket[i];
        }
      }
      return null;
    },

    clear: function () {
      try { if (backend) backend.removeItem(STORAGE_KEY); } catch (e) { /* silent */ }
      this._notify();
    }
  };

  /* ==========================================================================
     CART — native Shopify cart mirror (Buy-now section). Seeded synchronously
     from a server-rendered JSON island (data-ica-cart-seed) so the badge is
     correct on first paint with no fetch, then kept live via /cart.js and
     mutated via /cart/change.js. Holds only checkout-mode line items.
     ========================================================================*/
  var Cart = {
    listeners: [],
    _state: { item_count: 0, items: [] },
    // Currency symbol learned from the server-rendered seed (Liquid `money`
    // filter), retained so formatting survives /cart/change.js (which returns
    // only cents). Defaults to the Ghana cedi sign (GH primary market).
    _symbol: '₵',

    on: function (fn) { if (typeof fn === 'function') this.listeners.push(fn); },

    symbol: function () { return this._symbol; },

    _notify: function () {
      for (var i = 0; i < this.listeners.length; i++) {
        try { this.listeners[i](this._state); } catch (e) { /* silent */ }
      }
    },

    seed: function () {
      // Read the server-rendered cart island (count + line items) for an
      // accurate first paint. Falls back to the badge's data-server-cart-count.
      var island = document.querySelector('[data-ica-cart-seed]');
      if (island) {
        try {
          var parsed = JSON.parse(island.textContent || '{}');
          if (parsed && typeof parsed.item_count === 'number') {
            this._state = { item_count: parsed.item_count, items: parsed.items || [] };
            if (parsed.currency_symbol) { this._symbol = parsed.currency_symbol; }
            else if (parsed.items && parsed.items[0] && parsed.items[0].price_formatted) {
              var m = String(parsed.items[0].price_formatted).match(/^[^\d.,\s]+/);
              if (m) this._symbol = m[0];
            }
            return;
          }
        } catch (e) { /* fall through */ }
      }
      var badge = document.querySelector('[data-server-cart-count]');
      if (badge) {
        var n = parseInt(badge.getAttribute('data-server-cart-count'), 10);
        if (!isNaN(n)) this._state.item_count = n;
      }
    },

    count: function () { return this._state.item_count || 0; },
    items: function () { return this._state.items || []; },

    _apply: function (cart) {
      if (!cart) return;
      this._state = {
        item_count: cart.item_count || 0,
        items: (cart.items || []).map(function (li) {
          return {
            key: li.key,
            id: li.id,
            handle: li.handle,
            title: li.product_title || li.title,
            variant_title: (li.variant_title && li.variant_title !== 'Default Title') ? li.variant_title : '',
            qty: li.quantity,
            line_price: li.line_price,           // cents
            price: li.price,                     // cents (unit)
            price_formatted: li.price_formatted || '',
            image: li.image || (li.featured_image && li.featured_image.url) || '',
            url: li.url || ('/products/' + li.handle)
          };
        })
      };
      this._notify();
    },

    // Refresh from /cart.js (full mirror, includes per-line price formatting
    // via the line item properties we don't get from change.js's leaner shape).
    refresh: function () {
      var self = this;
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) { if (cart) self._apply(cart); })
        .catch(function () { /* silent — keep last known state */ });
    },

    // Change a line quantity by its line key. Returns the refreshed promise.
    changeQty: function (key, qty) {
      var self = this;
      qty = parseInt(qty, 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) { if (cart) self._apply(cart); })
        .catch(function () { /* silent */ });
    },

    remove: function (key) { return this.changeQty(key, 0); }
  };

  /* ==========================================================================
     COMBINED COUNT — header badge = native cart + localStorage quote basket.
     ========================================================================*/
  function combinedCount() {
    return Cart.count() + Store.size();
  }

  /* ==========================================================================
     EVENT FIRING — thin wrappers around window.icaEvents.fire
     ========================================================================*/
  function fireEvent(eventType, payload) {
    if (window.icaEvents && typeof window.icaEvents.fire === 'function') {
      try { window.icaEvents.fire(eventType, payload || {}); } catch (e) { /* silent */ }
    }
  }

  /* ==========================================================================
     BADGE — mobile inline + desktop floating; count + visibility
     ========================================================================*/
  var Badge = {
    init: function () {
      var self = this;
      this.render();
      Store.on(function () { self.render(); });
      Cart.on(function () { self.render(); });
      // Click handlers — any badge-tagged element (header cart icon) opens the
      // unified drawer. The header trigger carries data-ica-cart-trigger too;
      // both resolve to the same Drawer.open.
      var els = document.querySelectorAll('[data-ica-quote-badge]');
      for (var i = 0; i < els.length; i++) {
        els[i].addEventListener('click', function (e) {
          e.preventDefault();
          Drawer.open('header_cart');
        });
      }
    },
    // Combined-count render. The count element on the HEADER cart icon is the
    // source of truth; legacy floating badges (if any remain) hide at 0.
    render: function () {
      var count = combinedCount();
      var els = document.querySelectorAll('[data-ica-quote-badge]');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        var countEl = el.querySelector('[data-ica-quote-badge-count]');
        var isHeaderTrigger = el.hasAttribute('data-ica-cart-trigger');
        if (countEl) {
          countEl.textContent = count;
          if (count > 0) { countEl.removeAttribute('hidden'); }
          else { countEl.setAttribute('hidden', ''); }
        }
        var label = 'Cart, ' + count + ' item' + (count === 1 ? '' : 's');
        if (isHeaderTrigger) {
          // Header cart is always visible; only its count chip toggles.
          el.setAttribute('aria-label', label);
        } else if (count > 0) {
          el.removeAttribute('hidden');
          el.setAttribute('aria-label', label);
        } else {
          el.setAttribute('hidden', '');
        }
      }
    },
    pulse: function () {
      var els = document.querySelectorAll('[data-ica-quote-badge]');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        el.classList.remove('is-pulsing');
        // Force reflow so the animation restarts
        // eslint-disable-next-line no-unused-expressions
        void el.offsetWidth;
        el.classList.add('is-pulsing');
      }
    }
  };

  /* ==========================================================================
     TOAST — slide in, auto-dismiss after 3s, "View quote" link
     ========================================================================*/
  var Toast = {
    el: null,
    timer: null,
    init: function () {
      this.el = document.querySelector('[data-ica-quote-toast]');
      if (!this.el) return;
      var self = this;
      var closeBtn = this.el.querySelector('[data-ica-quote-toast-close]');
      if (closeBtn) closeBtn.addEventListener('click', function () { self.hide(); });
      var link = this.el.querySelector('[data-ica-quote-toast-link]');
      if (link) link.addEventListener('click', function (e) {
        e.preventDefault();
        self.hide();
        Drawer.open('toast_link');
      });
    },
    show: function (opts) {
      if (!this.el) return;
      opts = opts || {};
      var bodyEl = this.el.querySelector('[data-ica-quote-toast-body]');
      if (bodyEl) {
        var basket = Store.getBasket();
        var size = basket.length;
        if (opts.kind === 'qty_updated') {
          bodyEl.innerHTML = '<strong>Quantity updated</strong> · ' +
            escapeHtml(opts.title || 'Item') + ' (×' + (opts.qty || 1) + ')';
        } else {
          bodyEl.innerHTML = '<strong>Added to cart</strong> · ' + size + ' quote item' + (size === 1 ? '' : 's');
        }
      }
      this.el.classList.add('is-visible');
      this.el.removeAttribute('hidden');
      // Body class lets CSS coordinate with the PDP sticky CTA bar:
      // .ica-pdp-sticky-cta.is-active slides down while this is set.
      document.body.classList.add('ica-quote-toast-active');
      if (this.timer) clearTimeout(this.timer);
      var self = this;
      this.timer = setTimeout(function () { self.hide(); }, 3000);
    },
    hide: function () {
      if (!this.el) return;
      this.el.classList.remove('is-visible');
      document.body.classList.remove('ica-quote-toast-active');
      if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    }
  };

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ==========================================================================
     DRAWER — open/close, focus trap, render basket, line items, footer
     ========================================================================*/
  var Drawer = {
    el: null,
    backdrop: null,
    body: null,
    foot: null,
    titleCount: null,
    lastFocus: null,
    qtyTimer: {},

    init: function () {
      this.el = document.querySelector('[data-ica-quote-drawer]');
      this.backdrop = document.querySelector('[data-ica-quote-backdrop]');
      if (!this.el) return;

      this.body = this.el.querySelector('[data-ica-quote-drawer-body]');
      this.foot = this.el.querySelector('[data-ica-quote-drawer-foot]');
      this.titleCount = this.el.querySelector('[data-ica-quote-drawer-title-count]');

      var self = this;
      var closeBtns = this.el.querySelectorAll('[data-ica-quote-drawer-close]');
      for (var i = 0; i < closeBtns.length; i++) {
        closeBtns[i].addEventListener('click', function () { self.close(); });
      }

      if (this.backdrop) {
        this.backdrop.addEventListener('click', function () { self.close(); });
      }

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen()) self.close();
      });

      // Continue Browsing
      var contBtn = this.el.querySelector('[data-ica-quote-drawer-continue]');
      if (contBtn) contBtn.addEventListener('click', function () { self.close(); });

      // Footer: "Request quote" (RFQ items → quote form) + "Checkout" (native).
      var quoteBtn = this.el.querySelector('[data-ica-quote-drawer-submit]');
      if (quoteBtn) quoteBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.requestQuote();
      });
      var checkoutBtn = this.el.querySelector('[data-ica-cart-checkout]');
      if (checkoutBtn) checkoutBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.checkout();
      });

      // Re-render on either store change while open.
      Store.on(function () { if (self.isOpen()) self.render(); });
      Cart.on(function () { if (self.isOpen()) self.render(); });
      this.render();
    },

    isOpen: function () {
      return this.el && this.el.classList.contains('is-open');
    },

    open: function (sourceTrigger) {
      if (!this.el) return;
      this.lastFocus = document.activeElement;
      this.render();
      // Pull the freshest native-cart state in the background; re-renders the
      // Buy-now section in place when it resolves (no flash — seed already drew).
      Cart.refresh();
      this.el.removeAttribute('hidden');
      if (this.backdrop) this.backdrop.removeAttribute('hidden');
      // Next frame so transition runs
      var self = this;
      requestAnimationFrame(function () {
        self.el.classList.add('is-open');
        if (self.backdrop) self.backdrop.classList.add('is-visible');
        document.body.classList.add('ica-quote-drawer-open');
        // Focus the close button so SR users hear "Close cart"
        var closeBtn = self.el.querySelector('[data-ica-quote-drawer-close]');
        if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus();
      });
      fireEvent('quote_basket_view', {
        basket_size: Store.size(),
        cart_size: Cart.count(),
        source_trigger: sourceTrigger || 'header_cart'
      });
    },

    close: function () {
      if (!this.el) return;
      this.el.classList.remove('is-open');
      if (this.backdrop) this.backdrop.classList.remove('is-visible');
      document.body.classList.remove('ica-quote-drawer-open');
      var self = this;
      setTimeout(function () {
        if (!self.isOpen()) {
          self.el.setAttribute('hidden', '');
          if (self.backdrop) self.backdrop.setAttribute('hidden', '');
        }
      }, 250);
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        try { this.lastFocus.focus(); } catch (e) { /* silent */ }
      }
    },

    // "Request quote" — routes ONLY the RFQ (localStorage) items through the
    // existing quote-form flow. Native cart / checkout items are untouched.
    requestQuote: function () {
      var basket = Store.getBasket();
      if (basket.length === 0) return;
      var handles = [];
      var brands = [];
      var lines = [];
      for (var i = 0; i < basket.length; i++) {
        handles.push(basket[i].handle);
        if (basket[i].brand && brands.indexOf(basket[i].brand) === -1) brands.push(basket[i].brand);
        if (basket[i].product_line && lines.indexOf(basket[i].product_line) === -1) lines.push(basket[i].product_line);
      }
      fireEvent('quote_basket_submitted', {
        basket_size: basket.length,
        product_handles: handles,
        brands: brands,
        product_lines: lines,
        total_estimated_value_at_submit: null
      });
      // Hand off to the coating-expert quote form (B3 Fix 4 — new handle).
      // ica-quote-form.js reads ica_quote_basket_v1 + ?intent=quote and
      // pre-fills the Project Basket panel exactly as before.
      window.location.href = QUOTE_PAGE + '?intent=quote';
    },

    // "Checkout" — sends ONLY the native (checkout-mode) cart items to Shopify
    // checkout. RFQ items live in localStorage, never in the Shopify cart, so
    // checkout can never include a quote item.
    checkout: function () {
      if (Cart.count() === 0) return;
      window.location.href = '/checkout';
    },

    render: function () {
      if (!this.el || !this.body) return;
      var basket = Store.getBasket();      // RFQ / quote items
      var cartItems = Cart.items();        // native checkout items
      var quoteCount = basket.length;
      var cartCount = Cart.count();
      var total = quoteCount + cartCount;

      if (this.titleCount) {
        this.titleCount.textContent = total === 0 ? '' : '· ' + total + ' item' + (total === 1 ? '' : 's');
      }

      // Empty state — no items in EITHER section.
      if (total === 0) {
        this.body.innerHTML = this._renderEmpty();
        if (this.foot) this.foot.setAttribute('hidden', '');
        return;
      }

      var html = '';

      // SECTION 1 — Quote items (only when present).
      if (quoteCount > 0) {
        html += '<section class="ica-cart-section" data-ica-cart-section="quote" aria-label="Quote items">' +
          '<div class="ica-cart-section-head">' +
            '<span class="ica-cart-section-title">Quote items</span>' +
            '<span class="ica-cart-section-count">' + quoteCount + '</span>' +
          '</div>';
        for (var i = 0; i < basket.length; i++) {
          html += this._renderLine(basket[i]);
        }
        html += '</section>';
      }

      // SECTION 2 — Buy now items (only when present).
      if (cartCount > 0) {
        html += '<section class="ica-cart-section" data-ica-cart-section="buy" aria-label="Buy now items">' +
          '<div class="ica-cart-section-head">' +
            '<span class="ica-cart-section-title">Buy now</span>' +
            '<span class="ica-cart-section-count">' + cartCount + '</span>' +
          '</div>';
        for (var j = 0; j < cartItems.length; j++) {
          html += this._renderCartLine(cartItems[j]);
        }
        html += '</section>';
      }

      this.body.innerHTML = html;

      // Footer: show the whole footer if anything is present; toggle each
      // button by its section's presence.
      if (this.foot) {
        this.foot.removeAttribute('hidden');
        var quoteBtn = this.foot.querySelector('[data-ica-quote-drawer-submit]');
        var checkoutBtn = this.foot.querySelector('[data-ica-cart-checkout]');
        if (quoteBtn) {
          if (quoteCount > 0) { quoteBtn.removeAttribute('hidden'); }
          else { quoteBtn.setAttribute('hidden', ''); }
        }
        if (checkoutBtn) {
          if (cartCount > 0) {
            checkoutBtn.removeAttribute('hidden');
            var totalEl = checkoutBtn.querySelector('[data-ica-cart-checkout-total]');
            if (totalEl) totalEl.textContent = this._cartTotalFormatted();
          } else {
            checkoutBtn.setAttribute('hidden', '');
          }
        }
      }

      this._wireLineHandlers();
      this._wireCartLineHandlers();
    },

    _cartTotalFormatted: function () {
      var items = Cart.items();
      var cents = 0;
      for (var i = 0; i < items.length; i++) { cents += (items[i].line_price || 0); }
      return this._formatMoney(cents, items[0] && items[0].price_formatted);
    },

    _renderEmpty: function () {
      return '' +
        '<div class="ica-quote-drawer-empty">' +
          '<svg class="ica-quote-drawer-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
            '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/>' +
            '<path d="M3 6h18"/>' +
            '<path d="M16 10a4 4 0 0 1-8 0"/>' +
          '</svg>' +
          '<h3 class="ica-quote-drawer-empty-title">Your cart is empty.</h3>' +
          '<p class="ica-quote-drawer-empty-text">Browse the catalog and add products. Project items go to a quote; in-stock items can check out — all from this one cart.</p>' +
          '<a class="ica-quote-drawer-empty-cta" href="/collections/all">Browse all products</a>' +
          '<a class="ica-quote-drawer-empty-link" href="' + QUOTE_PAGE + '">Talk to a coating expert →</a>' +
        '</div>';
    },

    _brandClass: function (brand) {
      var h = (brand || '').toLowerCase();
      if (h.indexOf('nofire') !== -1) return ' is-nofire';
      if (h.indexOf('beyond') !== -1) return ' is-beyond';
      return '';
    },

    _renderLine: function (item) {
      var handle = escapeHtml(item.handle);
      var title = escapeHtml(item.title);
      var brand = escapeHtml(item.brand);
      var line = escapeHtml(item.product_line);
      var img = escapeHtml(item.image_src);
      var qty = parseInt(item.qty, 10) || 1;
      var notes = item.notes || '';
      var notesLen = notes.length;
      var notesExpanded = notes.length > 0;
      var brandClass = this._brandClass(item.brand);
      var eyebrowParts = [];
      if (brand) eyebrowParts.push(brand);
      if (line) eyebrowParts.push(line);
      var eyebrow = eyebrowParts.join(' · ');

      return '' +
      '<div class="ica-quote-line" data-ica-quote-line data-handle="' + handle + '">' +
        '<div class="ica-quote-line-top">' +
          '<div class="ica-quote-line-thumb">' +
            (img ? '<img src="' + img + '" alt="' + title + ' thumbnail" width="80" height="80" loading="lazy" decoding="async">' : '') +
          '</div>' +
          '<div class="ica-quote-line-info">' +
            (eyebrow ? '<div class="ica-quote-line-eyebrow">' + eyebrow + '</div>' : '') +
            '<h4 class="ica-quote-line-title"><a class="ica-quote-line-title-link" href="/products/' + handle + '">' + title + '</a></h4>' +
            (brand ? '<span class="ica-quote-line-brand-badge' + brandClass + '">' + brand + '</span>' : '') +
          '</div>' +
          '<button class="ica-quote-line-remove" type="button" data-ica-quote-remove aria-label="Remove ' + title + ' from quote">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="ica-quote-line-controls">' +
          '<div class="ica-quote-qty-stepper" role="group" aria-label="Quantity for ' + title + '">' +
            '<button class="ica-quote-qty-btn" type="button" data-ica-quote-qty-down aria-label="Decrease quantity"' + (qty <= 1 ? ' disabled' : '') + '>−</button>' +
            '<input class="ica-quote-qty-input" type="number" value="' + qty + '" min="1" max="999" inputmode="numeric" aria-label="Quantity" data-ica-quote-qty-input>' +
            '<button class="ica-quote-qty-btn" type="button" data-ica-quote-qty-up aria-label="Increase quantity"' + (qty >= 999 ? ' disabled' : '') + '>+</button>' +
          '</div>' +
          '<button class="ica-quote-notes-toggle" type="button" data-ica-quote-notes-toggle aria-expanded="' + (notesExpanded ? 'true' : 'false') + '">' +
            '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
              (notesExpanded ? '<path d="M5 13l4 4L19 7"/>' : '<path d="M12 5v14M5 12h14"/>') +
            '</svg>' +
            (notesExpanded ? 'Notes (' + notesLen + ' chars)' : 'Add notes') +
          '</button>' +
        '</div>' +
        '<div class="ica-quote-notes-wrap"' + (notesExpanded ? '' : ' hidden') + '>' +
          '<label class="ica-quote-notes-label" for="ica-quote-notes-' + handle + '">Notes</label>' +
          '<textarea class="ica-quote-notes-textarea" id="ica-quote-notes-' + handle + '" data-ica-quote-notes maxlength="500" placeholder="e.g., 80 microns DFT, two coats, on blast-cleaned steel">' + escapeHtml(notes) + '</textarea>' +
          '<div class="ica-quote-notes-meta">' +
            '<span>Saves on blur</span>' +
            '<span><strong data-ica-quote-notes-count>' + notesLen + '</strong> / 500</span>' +
          '</div>' +
        '</div>' +
      '</div>';
    },

    // Buy-now (native cart) line item. Shows price; qty stepper + remove drive
    // /cart/change.js. Keyed on the Shopify line `key`, not the product handle,
    // so variants of the same product stay distinct.
    _renderCartLine: function (item) {
      var key = escapeHtml(item.key);
      var title = escapeHtml(item.title);
      var variant = escapeHtml(item.variant_title);
      var img = escapeHtml(item.image);
      var url = escapeHtml(item.url);
      var qty = parseInt(item.qty, 10) || 1;
      var linePrice = this._formatMoney(item.line_price, item.price_formatted);

      return '' +
      '<div class="ica-quote-line ica-cart-line" data-ica-cart-line data-key="' + key + '">' +
        '<div class="ica-quote-line-top">' +
          '<div class="ica-quote-line-thumb">' +
            (img ? '<img src="' + img + '" alt="' + title + ' thumbnail" width="80" height="80" loading="lazy" decoding="async">' : '') +
          '</div>' +
          '<div class="ica-quote-line-info">' +
            '<h4 class="ica-quote-line-title"><a class="ica-quote-line-title-link" href="' + url + '">' + title + '</a></h4>' +
            (variant ? '<div class="ica-quote-line-eyebrow">' + variant + '</div>' : '') +
            '<span class="ica-cart-line-price">' + linePrice + '</span>' +
          '</div>' +
          '<button class="ica-quote-line-remove" type="button" data-ica-cart-remove aria-label="Remove ' + title + ' from cart">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="ica-quote-line-controls">' +
          '<div class="ica-quote-qty-stepper" role="group" aria-label="Quantity for ' + title + '">' +
            '<button class="ica-quote-qty-btn" type="button" data-ica-cart-qty-down aria-label="Decrease quantity"' + (qty <= 1 ? ' disabled' : '') + '>−</button>' +
            '<input class="ica-quote-qty-input" type="number" value="' + qty + '" min="1" max="999" inputmode="numeric" aria-label="Quantity" data-ica-cart-qty-input>' +
            '<button class="ica-quote-qty-btn" type="button" data-ica-cart-qty-up aria-label="Increase quantity">+</button>' +
          '</div>' +
          '<span class="ica-cart-line-badge">In stock</span>' +
        '</div>' +
      '</div>';
    },

    _formatMoney: function (cents, sampleFormatted) {
      if (cents == null) return '';
      var symbol = Cart.symbol();
      if (sampleFormatted) {
        var m = String(sampleFormatted).match(/^[^\d.,\s]+/);
        if (m) symbol = m[0];
      }
      return symbol + (cents / 100).toFixed(2);
    },

    _wireCartLineHandlers: function () {
      var self = this;
      var lines = this.body.querySelectorAll('[data-ica-cart-line]');
      for (var i = 0; i < lines.length; i++) {
        (function (line) {
          var key = line.getAttribute('data-key');
          var input = line.querySelector('[data-ica-cart-qty-input]');
          var downBtn = line.querySelector('[data-ica-cart-qty-down]');
          var upBtn = line.querySelector('[data-ica-cart-qty-up]');
          var removeBtn = line.querySelector('[data-ica-cart-remove]');

          function commit(newQty) {
            if (self.qtyTimer['cart_' + key]) clearTimeout(self.qtyTimer['cart_' + key]);
            self.qtyTimer['cart_' + key] = setTimeout(function () {
              Cart.changeQty(key, newQty);
            }, QTY_DEBOUNCE_MS);
          }

          if (downBtn) downBtn.addEventListener('click', function () {
            var cur = parseInt(input.value, 10) || 1;
            var next = Math.max(1, cur - 1);
            input.value = next;
            commit(next);
          });
          if (upBtn) upBtn.addEventListener('click', function () {
            var cur = parseInt(input.value, 10) || 1;
            var next = Math.min(999, cur + 1);
            input.value = next;
            commit(next);
          });
          if (input) {
            input.addEventListener('blur', function () {
              var v = parseInt(input.value, 10);
              if (isNaN(v) || v < 1) { v = 1; input.value = 1; }
              if (v > 999) { v = 999; input.value = 999; }
              Cart.changeQty(key, v);
            });
          }
          if (removeBtn) removeBtn.addEventListener('click', function () {
            Cart.remove(key);
          });
        })(lines[i]);
      }
    },

    _wireLineHandlers: function () {
      var self = this;
      var lines = this.body.querySelectorAll('[data-ica-quote-line]');
      for (var i = 0; i < lines.length; i++) {
        (function (line) {
          var handle = line.getAttribute('data-handle');

          var removeBtn = line.querySelector('[data-ica-quote-remove]');
          if (removeBtn) removeBtn.addEventListener('click', function () {
            var item = Store.getItem(handle);
            Store.removeItem(handle);
            fireEvent('quote_basket_remove', {
              product_gid: item ? item.product_gid : null,
              product_handle: handle,
              basket_size_after: Store.size()
            });
          });

          var downBtn = line.querySelector('[data-ica-quote-qty-down]');
          var upBtn = line.querySelector('[data-ica-quote-qty-up]');
          var input = line.querySelector('[data-ica-quote-qty-input]');

          function commit(newQty) {
            if (self.qtyTimer[handle]) clearTimeout(self.qtyTimer[handle]);
            self.qtyTimer[handle] = setTimeout(function () {
              Store.setQty(handle, newQty);
            }, QTY_DEBOUNCE_MS);
          }

          if (downBtn) downBtn.addEventListener('click', function () {
            var cur = parseInt(input.value, 10) || 1;
            var next = Math.max(1, cur - 1);
            input.value = next;
            commit(next);
          });
          if (upBtn) upBtn.addEventListener('click', function () {
            var cur = parseInt(input.value, 10) || 1;
            var next = Math.min(999, cur + 1);
            input.value = next;
            commit(next);
          });
          if (input) {
            input.addEventListener('input', function () {
              var v = parseInt(input.value, 10);
              if (isNaN(v)) v = 1;
              if (v < 1) v = 1;
              if (v > 999) v = 999;
              commit(v);
            });
            input.addEventListener('blur', function () {
              var v = parseInt(input.value, 10);
              if (isNaN(v) || v < 1) { v = 1; input.value = 1; }
              if (v > 999) { v = 999; input.value = 999; }
              Store.setQty(handle, v);
            });
          }

          var notesToggle = line.querySelector('[data-ica-quote-notes-toggle]');
          var notesWrap = line.querySelector('.ica-quote-notes-wrap');
          var notesTextarea = line.querySelector('[data-ica-quote-notes]');
          var notesCount = line.querySelector('[data-ica-quote-notes-count]');
          if (notesToggle && notesWrap) {
            notesToggle.addEventListener('click', function () {
              var expanded = !notesWrap.hasAttribute('hidden');
              if (expanded) {
                notesWrap.setAttribute('hidden', '');
                notesToggle.setAttribute('aria-expanded', 'false');
              } else {
                notesWrap.removeAttribute('hidden');
                notesToggle.setAttribute('aria-expanded', 'true');
                if (notesTextarea) notesTextarea.focus();
              }
            });
          }
          if (notesTextarea) {
            notesTextarea.addEventListener('input', function () {
              if (notesCount) notesCount.textContent = notesTextarea.value.length;
            });
            notesTextarea.addEventListener('blur', function () {
              Store.setNotes(handle, notesTextarea.value);
            });
          }
        })(lines[i]);
      }
    }
  };

  /* ==========================================================================
     ADD — wire Add-to-Quote button clicks
     ========================================================================*/
  var Add = {
    init: function () {
      this._wireExisting();
      this._refreshButtonStates(Store.getBasket());
      var self = this;
      Store.on(function (basket) { self._refreshButtonStates(basket); });

      // Helper link inside PDP buy box: "adjust quantity in drawer"
      var helperLinks = document.querySelectorAll('[data-ica-quote-drawer-trigger]');
      for (var i = 0; i < helperLinks.length; i++) {
        helperLinks[i].addEventListener('click', function (e) {
          e.preventDefault();
          Drawer.open('deep_link');
        });
      }
    },
    _wireExisting: function () {
      var btns = document.querySelectorAll('[data-ica-quote-add]');
      for (var i = 0; i < btns.length; i++) {
        (function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            Add._handle(btn);
          });
        })(btns[i]);
      }
    },
    _handle: function (btn) {
      // Silent degradation if storage is unavailable
      if (!storageAvailable) {
        var handle = btn.getAttribute('data-product-handle');
        var brand = btn.getAttribute('data-product-brand');
        var url = QUOTE_PAGE + '?product=' + encodeURIComponent(handle || '');
        if (brand) url += '&brand=' + encodeURIComponent(brand);
        window.location.href = url;
        return;
      }
      var sourcePage = btn.getAttribute('data-source-page') || 'pdp';
      var item = {
        handle: btn.getAttribute('data-product-handle'),
        product_gid: btn.getAttribute('data-product-gid'),
        title: btn.getAttribute('data-product-title') || btn.getAttribute('data-product-handle'),
        brand: btn.getAttribute('data-product-brand') || '',
        product_line: btn.getAttribute('data-product-line') || '',
        image_src: btn.getAttribute('data-product-image') || ''
      };
      var res = Store.addItem(item);
      if (res.result === 'invalid') return;

      fireEvent('quote_basket_add', {
        product_gid: item.product_gid,
        product_handle: item.handle,
        sku: btn.getAttribute('data-product-sku') || null,
        brand: item.brand,
        product_line: item.product_line,
        source_page: sourcePage,
        basket_size_after: Store.size()
      });

      Badge.pulse();
      if (res.result === 'incremented') {
        Toast.show({ kind: 'qty_updated', title: res.item.title, qty: res.item.qty });
      } else {
        Toast.show({ kind: 'added' });
      }
    },
    _refreshButtonStates: function (basket) {
      var handles = {};
      for (var i = 0; i < basket.length; i++) {
        handles[basket[i].handle] = basket[i].qty;
      }
      var btns = document.querySelectorAll('[data-ica-quote-add]');
      for (var j = 0; j < btns.length; j++) {
        var btn = btns[j];
        var h = btn.getAttribute('data-product-handle');
        var icon = btn.querySelector('[data-ica-quote-add-icon]');
        var pill = btn.querySelector('[data-ica-quote-add-qty]');
        var pillNum = btn.querySelector('[data-ica-quote-add-qty-num]');
        if (handles[h]) {
          // r5 Fix #1+2+3 — single-button "Added" pattern:
          // same orange bg, prepend check icon, append navy qty pill.
          // No second sibling button; no green color swap.
          btn.setAttribute('data-in-basket', 'true');
          btn.classList.add('ica-quote-add-btn--added');
          if (icon) icon.removeAttribute('hidden');
          if (pill) pill.removeAttribute('hidden');
          if (pillNum) pillNum.textContent = String(handles[h]);
        } else {
          btn.setAttribute('data-in-basket', 'false');
          btn.classList.remove('ica-quote-add-btn--added');
          if (icon) icon.setAttribute('hidden', '');
          if (pill) pill.setAttribute('hidden', '');
          if (pillNum) pillNum.textContent = '1';
        }
      }
      // Toggle helper line visibility (PDP)
      var helpers = document.querySelectorAll('[data-ica-quote-add-helper]');
      for (var k = 0; k < helpers.length; k++) {
        var helper = helpers[k];
        var handle = helper.getAttribute('data-product-handle');
        if (handle && handles[handle]) {
          helper.removeAttribute('hidden');
        } else {
          helper.setAttribute('hidden', '');
        }
      }
    }
  };

  /* ==========================================================================
     BOOT
     ========================================================================*/
  function boot() {
    Cart.seed();      // synchronous first-paint count from the server island
    Drawer.init();
    Badge.init();
    Toast.init();
    Add.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose for QA + future modules
  window.icaQuote = {
    Store: Store,
    Cart: Cart,
    Drawer: Drawer,
    Badge: Badge,
    Toast: Toast,
    Add: Add,
    combinedCount: combinedCount,
    storageAvailable: storageAvailable
  };
})();
